"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo } from "react";

// Import new small stores
import { ErrorBoundary } from "@/components/error-boundary";
import { useMapBoundsUpdater } from "@/hooks/use-map-bounds-updater";
import { useMapMarkerZoomUpdater } from "@/hooks/use-map-marker-zoom-updater";
import { useIsMobile } from "@/hooks/use-mobile";
import { useSearchMode } from "@/hooks/use-search-mode";
import { useUrlFilters } from "@/hooks/use-url-filters";
import { mapDefaults } from "@/info/map-defaults";
import { useSearchPageMapData } from "@/providers/search";
import { Map, useMap } from "@vis.gl/react-google-maps";
import { AutoCenterUser } from "./auto-center-user";
import { CenterMarker } from "./center-marker";
import { MapConfigurationError } from "./fallback";
import { Markers } from "./markers";
import { LocationOverlay } from "./nav-overlay";
import { ProjectMarkers } from "./project-markers";
import { UnifiedMapControls } from "./unified-map-controls";
const {
  center,
  zoom
} = mapDefaults;
interface SearchMapProps {
  mapId?: string;
  showViewSwitcher?: boolean;
  view?: "map" | "list";
  onViewChange?: (v: "map" | "list") => void;
  city?: string;
  mapControlsClassName?: string;
}

// Component to use the bounds fetching hook - must be inside Map context
function MapBoundsHandler() {
  useMapBoundsUpdater();
  useMapMarkerZoomUpdater();
  return null;
}

// Component to sync global state with Map instance
function MapStateSync() {
  const $ = _c(11);
  const map = useMap();
  const mapTypeId = useSearchPageMapData(_temp);
  const isFullScreen = useSearchPageMapData(_temp2);
  const setIsFullScreen = useSearchPageMapData(_temp3);
  let t0;
  let t1;
  if ($[0] !== map || $[1] !== mapTypeId) {
    t0 = () => {
      if (map && mapTypeId) {
        map.setMapTypeId(mapTypeId);
      }
    };
    t1 = [map, mapTypeId];
    $[0] = map;
    $[1] = mapTypeId;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[4] !== isFullScreen || $[5] !== map) {
    t2 = () => {
      if (!map) {
        return;
      }
      const mapDiv = map.getDiv();
      if (isFullScreen) {
        if (!document.fullscreenElement) {
          mapDiv.requestFullscreen().catch(_temp4);
        }
      } else {
        if (document.fullscreenElement) {
          document.exitFullscreen();
        }
      }
    };
    t3 = [map, isFullScreen];
    $[4] = isFullScreen;
    $[5] = map;
    $[6] = t2;
    $[7] = t3;
  } else {
    t2 = $[6];
    t3 = $[7];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[8] !== setIsFullScreen) {
    t4 = () => {
      const handleFullscreenChange = () => {
        if (!document.fullscreenElement) {
          setIsFullScreen(false);
        }
      };
      document.addEventListener("fullscreenchange", handleFullscreenChange);
      return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
    t5 = [setIsFullScreen];
    $[8] = setIsFullScreen;
    $[9] = t4;
    $[10] = t5;
  } else {
    t4 = $[9];
    t5 = $[10];
  }
  useEffect(t4, t5);
  return null;
}
function _temp4(err) {
  console.error(`Error attempting to enable fullscreen: ${err.message}`);
}
function _temp3(state_1) {
  return state_1.setIsFullScreen;
}
function _temp2(state_0) {
  return state_0.isFullScreen;
}
function _temp(state) {
  return state.mapTypeId;
}
const MapViewFallback = t0 => {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="flex items-center justify-center h-full"><div className="text-center p-6"><div className="text-gray-800 font-semibold mb-2">Map View Error</div><p className="text-gray-600 text-sm">Unable to load map view. Try switching to list view.</p></div></div>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== children) {
    t2 = <div className="flex flex-col flex-1 w-full h-full relative bg-gray-100">{t1}{children}</div>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export function SearchMap(t0) {
  const $ = _c(33);
  const {
    mapId: t1,
    view: t2,
    onViewChange: t3
  } = t0;
  const mapId = t1 === undefined ? "sellerUnitListMap" : t1;
  const view = t2 === undefined ? "map" : t2;
  const onViewChange = t3 === undefined ? _temp5 : t3;
  const isMapDataFetching = useSearchPageMapData(_temp6);
  const mapCenter = useSearchPageMapData(_temp7);
  const mapZoom = useSearchPageMapData(_temp8);
  const mode = useSearchPageMapData(_temp9);
  const setMode = useSearchPageMapData(_temp10);
  useSearchMode();
  const {
    filters,
    setFilters
  } = useUrlFilters();
  const isMobile = useIsMobile();
  const effectiveCenter = mapCenter || center;
  const finalZoom = mapZoom || zoom;
  const effectiveZoom = finalZoom;
  let t4;
  if ($[0] !== filters.locations || $[1] !== filters.selectedLocationId) {
    t4 = filters.locations?.find(t5 => {
      const {
        id
      } = t5;
      return id === filters.selectedLocationId;
    }) || filters.locations?.[0];
    $[0] = filters.locations;
    $[1] = filters.selectedLocationId;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const selectedLocation = t4;
  let t5;
  let t6;
  if ($[3] !== filters.selectedLocationId || $[4] !== selectedLocation || $[5] !== setFilters || $[6] !== setMode) {
    t5 = () => {
      if (selectedLocation?.type === "project") {
        setMode("project");
        if (!filters.selectedLocationId && selectedLocation.id) {
          setFilters({
            selectedLocationId: selectedLocation.id
          });
        }
      }
    };
    t6 = [selectedLocation, filters.selectedLocationId, setFilters, setMode];
    $[3] = filters.selectedLocationId;
    $[4] = selectedLocation;
    $[5] = setFilters;
    $[6] = setMode;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAP_API_KEY;
  if (!apiKey) {
    let t7;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <div className="flex flex-col flex-1 w-full relative"><div className="map-container flex-grow w-full min-h-[400px]"><MapConfigurationError /></div></div>;
      $[9] = t7;
    } else {
      t7 = $[9];
    }
    return t7;
  }
  let t10;
  let t7;
  let t8;
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <MapBoundsHandler />;
    t8 = <MapStateSync />;
    t9 = <AutoCenterUser />;
    t10 = <CenterMarker />;
    $[10] = t10;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    t10 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t11;
  if ($[14] !== mode) {
    t11 = mode === "default" && <Markers />;
    $[14] = mode;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== mode) {
    t12 = mode === "project" && <ProjectMarkers />;
    $[16] = mode;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== isMapDataFetching) {
    t13 = <LocationOverlay isMapDataFetching={isMapDataFetching} />;
    $[18] = isMapDataFetching;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== filters || $[21] !== isMobile || $[22] !== onViewChange || $[23] !== view) {
    t14 = !isMobile && <UnifiedMapControls view={view} onViewChange={onViewChange} showViewToggles={!!onViewChange} filters={filters} className="absolute bottom-4 right-4" />;
    $[20] = filters;
    $[21] = isMobile;
    $[22] = onViewChange;
    $[23] = view;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== effectiveCenter || $[26] !== effectiveZoom || $[27] !== mapId || $[28] !== t11 || $[29] !== t12 || $[30] !== t13 || $[31] !== t14) {
    t15 = <ErrorBoundary fallback={MapViewFallback}><div className="flex flex-col flex-1 w-full h-full relative"><div className="map-container flex-grow w-full h-full relative"><div className="w-full h-full relative" id="map-container"><div className="w-full h-full bg-white focus:outline-none relative overflow-hidden"><Map defaultCenter={effectiveCenter} defaultZoom={effectiveZoom} disableDefaultUI={true} gestureHandling="greedy" id={mapId} mapId={mapId} reuseMaps={true}>{t7}{t8}{t9}{t10}{t11}{t12}{t13}{t14}</Map></div></div></div></div></ErrorBoundary>;
    $[25] = effectiveCenter;
    $[26] = effectiveZoom;
    $[27] = mapId;
    $[28] = t11;
    $[29] = t12;
    $[30] = t13;
    $[31] = t14;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  return t15;
}
function _temp10(state_3) {
  return state_3.setMode;
}
function _temp9(state_2) {
  return state_2.mode;
}
function _temp8(state_1) {
  return state_1.mapZoom;
}
function _temp7(state_0) {
  return state_0.mapCenter;
}
function _temp6(state) {
  return state.isMapDataFetching;
}
function _temp5() {}